import React, { useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { discoveryStore } from '../../DiscoveryStore';
import useDiscoveryStore from '../../DiscoveryStore/UseDiscoveryStore';
import { dropdownMenuItems } from '../Platform/NavigationBar/UseCaseMenu/Types';
import { AlreadyTranslated } from '@execonline-inc/translations';
import { T } from '../../Translations';
import { useCaseLink } from '../../ClientRoutes';
import History from '../../History';
import { UseCaseItem } from '../Dropdown/Types';
import LearningCollectionsSkeleton from './LearningCollectionsSkeleton';
import { sortByNameNaturally } from '../../Collections';

/**
 * Learning Collections Panel - displays user's learning collections in the dashboard right rail
 *
 * Features:
 * - Alphabetical sorting with natural number handling
 * - View More/View Less functionality for collections > 7
 * - Enrollment status indicators and program counts
 * - Performance optimized with React hooks (useMemo, useCallback)
 * - Header is rendered externally by SupplementalPanel for consistent styling
 *
 * See LEARNING_COLLECTIONS_FEATURE.md for detailed documentation
 */

const LearningCollectionsPanel: React.FC = observer(() => {
  useDiscoveryStore();

  const [showAll, setShowAll] = useState(false);
  const COLLECTION_LIMIT = 7;

  // Memoize the expensive sorting operation (moved before conditional returns)
  const collections = useMemo(() => {
    const rawCollections = discoveryStore.learningCollections;
    const unsortedCollections = dropdownMenuItems(rawCollections);
    return sortByNameNaturally(unsortedCollections);
  }, [discoveryStore.learningCollections]);

  // Memoize click handlers to prevent unnecessary re-renders
  const handleCollectionClick = useCallback(
    (item: UseCaseItem) => () => {
      const url = useCaseLink(item);
      History.push(url);
    },
    [],
  );

  const handleViewMoreClick = useCallback(() => {
    setShowAll(!showAll);
  }, [showAll]);

  // Memoize CSS classes to prevent string concatenation on every render
  // Dynamic height based on content - only limit height when expanded
  const containerClasses = useMemo(
    () => `flex flex-col gap-2 ${showAll ? 'max-h-[480px] overflow-y-auto' : ''}`,
    [showAll],
  );

  // Memoize data attribute processing to avoid repeated string operations
  const getCollectionDataName = useCallback(
    (name: string) => name.replace(/\s+/g, '-').toLowerCase(),
    [],
  );

  // Loading state handling
  if (discoveryStore.state.kind === 'discovery-store-loading') {
    return <LearningCollectionsSkeleton />;
  }

  // Empty state handling
  if (collections.length === 0) {
    return null;
  }

  const visibleCollections = showAll ? collections : collections.slice(0, COLLECTION_LIMIT);
  const hasMoreCollections = collections.length > COLLECTION_LIMIT;

  // CollectionMeta subcomponent for enrollment status and role display
  const CollectionMeta: React.FC<{ item: UseCaseItem }> = ({ item }) => {
    const { enrollmentStatus, registrationType } = item.content;
    const isAuditor =
      registrationType.kind === 'active-invitation' && registrationType.type === 'Auditor';

    return (
      <>
        {isAuditor && (
          <span className="mb-1 inline-flex h-4 items-center rounded-sm bg-teal-600 px-2 py-1 text-xs font-medium uppercase text-white">
            <T kind="Auditor" />
          </span>
        )}
        <div className="flex items-center gap-2 text-[13px] text-gray-600">
          <span className="text-gray-600">
            <T kind="{{count}} Program" count={item.content.programsCount} />
          </span>
          {enrollmentStatus.kind === 'currently-enrolled' && (
            <>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">
                <T kind="Currently Enrolled" />
              </span>
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="mb-6 rounded bg-white p-4" data-intercom-target="learning-collections-panel">
      <div className={containerClasses}>
        {visibleCollections.map((item, index) => (
          <button
            key={item.id}
            onClick={handleCollectionClick(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCollectionClick(item)();
              }
            }}
            className="w-full cursor-pointer rounded-sm bg-transparent px-3 py-2 text-left transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            title={item.name.text}
            aria-label={`Open learning collection: ${item.name.text}`}
            data-intercom-target="learning-collection"
            data-collection-id={item.content.uuid}
            data-collection-name={getCollectionDataName(item.name.text)}
            tabIndex={0}
          >
            <div className="mb-0.5 truncate text-sm font-medium text-gray-900">
              <AlreadyTranslated content={item.name} />
            </div>
            <CollectionMeta item={item} />
          </button>
        ))}
      </div>
      {hasMoreCollections && (
        <button
          onClick={handleViewMoreClick}
          className="mt-3 flex w-full items-center justify-center gap-1 rounded-sm bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          data-intercom-target="view-more-collections"
        >
          {showAll ? (
            <T kind="View Less" />
          ) : (
            <>
              <T kind="View More" /> (
              <T kind="{{count}} more" count={collections.length - COLLECTION_LIMIT} />)
            </>
          )}
        </button>
      )}
    </div>
  );
});

export default LearningCollectionsPanel;
